import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { addToCart } from '../../store/cartSlice';
import API_BASE_URL from '../../config';
import { getProductImageUrl } from '../../utils/imageUrl';
import './ProductCard.css';

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);

  const [isInWishlist, setIsInWishlist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    if (isAuthenticated && product?.id) {
      checkWishlistStatus();
    }
  }, [isAuthenticated, product?.id]);

  const checkWishlistStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/wishlist/check/${product.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIsInWishlist(!!data.in_wishlist);
      }
    } catch (error) {
      console.error('Error checking wishlist:', error);
    }
  };

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);

    window.clearTimeout(window.productToastTimer);
    window.productToastTimer = window.setTimeout(() => {
      setShowToast(false);
    }, 1800);
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login', {
        state: { from: '/products', message: 'Please login to add to cart' },
      });
      return;
    }

    if (loading) return;

    setLoading(true);

    const cartItem = {
      product_id: product.id,
      product_name: product.name,
      quantity: 1,
      price: product.sale_price || product.price,
      image: getProductImageUrl(product.images),
    };

    try {
      await dispatch(addToCart(cartItem)).unwrap();
      showToastMessage('Added to cart!');
    } catch (error) {
      showToastMessage('Error adding to cart');
    } finally {
      setLoading(false);
    }
  };

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login', {
        state: { from: '/products', message: 'Please login to add to wishlist' },
      });
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/wishlist/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ product_id: product.id }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsInWishlist(!!data.in_wishlist);
        showToastMessage(data.message || 'Wishlist updated');
      } else {
        showToastMessage(data.message || 'Error updating wishlist');
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      showToastMessage('Error updating wishlist');
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '₹0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return Number.isNaN(numPrice) ? '₹0.00' : `₹${numPrice.toFixed(2)}`;
  };

  const imageUrl = getProductImageUrl(product?.images);
  const price = product?.sale_price || product?.price;
  const originalPrice = product?.sale_price ? product?.price : null;

  const handleCardClick = () => navigate(`/products/${product.id}`);

  return (
    <div className="product-card" onClick={handleCardClick} role="button" tabIndex={0}>
      <div className="product-image">
        <img
          src={imageUrl}
          alt={product?.name || 'Product'}
          onError={(e) => {
            e.currentTarget.src =
              'https://via.placeholder.com/300x300?text=' +
              encodeURIComponent(product?.name || 'Product');
          }}
        />

        {product?.sale_price ? <span className="product-sale-badge">SALE</span> : null}

        <button
          type="button"
          className={`product-wishlist ${isInWishlist ? 'active' : ''}`}
          onClick={handleWishlistToggle}
          disabled={loading}
          aria-label={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <svg viewBox="0 0 24 24" fill={isInWishlist ? '#e74c3c' : 'none'} stroke="currentColor" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>
      </div>

      <div className="product-info">
        <span className="product-brand">{product?.brand || 'Premium Brand'}</span>
        <h3 className="product-name">{product?.name || 'Product Name'}</h3>

        <div className="product-price">
          {originalPrice ? (
            <>
              <span className="sale-price">{formatPrice(price)}</span>
              <span className="original-price">{formatPrice(originalPrice)}</span>
            </>
          ) : (
            <span className="regular-price">{formatPrice(price)}</span>
          )}
        </div>
      </div>

      <div className="product-actions">
        <button
          type="button"
          className="add-to-cart-btn"
          onClick={handleAddToCart}
          disabled={loading}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
          </svg>
          <span>{loading ? 'Adding...' : 'Add to Cart'}</span>
        </button>
      </div>

      {showToast && <div className="product-toast">{toastMessage}</div>}
    </div>
  );
};

export default ProductCard;